import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  cloudSourceService,
  CloudSourceListRow,
  CloudFileSyncRow,
  CloudSyncFileStatus,
} from '@/services/cloudSourceService';
import { formatFileSize } from '@/utils/kbUtils';
import { formatDateTime } from '@/utils/dateUtils';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';

interface ActivityModalProps {
  source: CloudSourceListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Parent re-fetches the source list - used after a self-heal recompute. */
  onRefresh?: () => void | Promise<void>;
  initialStatusFilter?: 'All' | CloudSyncFileStatus;
}

const STATUS_FILTERS: Array<{ value: 'All' | CloudSyncFileStatus; label: string }> = [
  { value: 'All', label: 'All statuses' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Synced', label: 'Synced' },
  { value: 'Skipped', label: 'Skipped' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Stale', label: 'Stale' },
];

const PAGE_SIZE = 25;

function statusVariant(
  status: CloudSyncFileStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'Synced':
      return 'default';
    case 'Failed':
      return 'destructive';
    case 'Stale':
    case 'Skipped':
      return 'outline';
    default:
      return 'secondary';
  }
}

const CloudSourceActivityModal: React.FC<ActivityModalProps> = ({
  source,
  open,
  onOpenChange,
  onRefresh,
  initialStatusFilter = 'All',
}) => {
  const [rows, setRows] = useState<CloudFileSyncRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Seed from the caller's deep-link choice. The card unmounts this modal on
  // close (`{showActivity && <Modal/>}`), so each open remounts and re-reads
  // the prop - no stale filter carries over between opens. The in-modal Select
  // still lets the user change it freely afterwards.
  const [statusFilter, setStatusFilter] = useState<'All' | CloudSyncFileStatus>(
    initialStatusFilter,
  );
  const [offset, setOffset] = useState(0);
  // Scrollable list container - we reset its scrollTop to 0 whenever the
  // page or filter changes so Prev/Next don't land the user mid-list.
  const listRef = useRef<HTMLDivElement>(null);
  // Cap self-heal at once per modal session - avoids any chance of a loop
  // when the parent re-renders us with a fresh source after recompute.
  const didSelfHealRef = useRef(false);
  // Snapshot the original claimed counts at open-time, so the discrepancy
  // check doesn't dep on the live `source` object identity.
  const sourceClaimedFilesAtOpenRef = useRef(
    (source.total_discovered ?? 0) +
      (source.synced_count ?? 0) +
      (source.failed_count ?? 0) +
      (source.skipped_count ?? 0) >
      0,
  );

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cloudSourceService.listFiles(source.name, {
        sync_status: statusFilter === 'All' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(res.rows);
      setTotal(res.total);

      // Self-heal: card claimed discovered > 0 at modal open but DB has
      // zero file rows - aggregates drifted (manual cleanup outside the
      // sync engine, etc.). Recompute once and bubble up.
      if (
        !didSelfHealRef.current &&
        statusFilter === 'All' &&
        offset === 0 &&
        res.total === 0 &&
        sourceClaimedFilesAtOpenRef.current
      ) {
        didSelfHealRef.current = true;
        try {
          await cloudSourceService.recomputeAggregates(source.name);
          toast.info('Counts were stale - refreshed from file rows');
          await onRefresh?.();
        } catch (err) {
          logger.warn('recomputeAggregates failed', err);
        }
      }
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Could not load activity');
      logger.error('Activity load failed', err);
      toast.error('Activity load failed', { description: msg });
    } finally {
      setLoading(false);
    }
  }, [source.name, statusFilter, offset, onRefresh]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [offset, statusFilter]);

  useEffect(() => {
    if (open) fetchPage();
  }, [open, fetchPage]);

  // Reset offset when filter changes
  useEffect(() => {
    setOffset(0);
  }, [statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground max-w-3xl">
        <DialogHeader>
          <DialogTitle>Activity - {source.provider}</DialogTitle>
          <DialogDescription>
            Per-file sync results. {total} file{total === 1 ? '' : 's'} total.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as 'All' | CloudSyncFileStatus)}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark">
              {STATUS_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPage}
            disabled={loading}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </div>

        <div
          ref={listRef}
          className="border rounded-lg divide-y max-h-[60vh] overflow-y-auto"
        >
          {loading && rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No files yet.
            </div>
          ) : (
            rows.map((row) => (
              <div key={row.name} className="p-3">
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{row.filename}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant={statusVariant(row.sync_status)}>
                          {row.sync_status}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {row.file_path && <span className="truncate">{row.file_path}</span>}
                      {row.file_size != null && (
                        <span>{formatFileSize(row.file_size)}</span>
                      )}
                      {row.synced_at && (
                        <span>
                          Synced {formatDateTime(row.synced_at)}
                        </span>
                      )}
                      {!row.synced_at && row.discovered_at && (
                        <span>
                          Discovered {formatDateTime(row.discovered_at)}
                        </span>
                      )}
                      {row.attempts > 0 && <span>{row.attempts} attempt(s)</span>}
                    </div>
                    {row.skip_reason && (
                      <p className="mt-1 text-xs text-destructive break-words">
                        {row.skip_reason}
                      </p>
                    )}
                    {row.last_error && (
                      <p className="mt-1 text-xs text-destructive break-words">
                        {row.last_error}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CloudSourceActivityModal;
